import { ChangeDetectionStrategy, Component, TemplateRef, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { finalize, fromEvent, takeUntil } from 'rxjs';

import { BaseTemplateEditorComponent } from '../base-template-editor/base-template-editor.component';
import { TEMPLATE_EDITOR_V2_IMPORTS } from './template-editor-v2.imports';
import { LOCKED_BLOCK_NAMES } from './sidebar-v2/sidebar-v2.component';
import { Helper } from '../../../utils/helper';
import {
  INITIAL_REVIEW_STATE,
  ReviewModeV2,
  ReviewStateV2,
  ReviewStoreV2,
} from './review/review-store-v2.service';
import { EditorHistoryV2 } from './history/editor-history-v2.service';
import { EditorSnapshotV2 } from '../../../models/other/editor-snapshot-v2.interface';
import BlockModel from '../../../models/email-template/block-model.interface';

type RightPanelTabV2 = 'properties' | 'comments';
type DeviceModeV2 = 'desktop' | 'mobile';
type VersionDialogV2 = { mode: 'create' | 'restore'; target: number };

/**
 * Visual-only redesign of the shared template editor (see template-editor.component.ts),
 * built for the Email channel per the Content Rocket mockup. Extends the same base class
 * so ALL business logic / API calls / state management is 100% reused unchanged; only a
 * handful of new, purely-presentational fields are added below (selection, collapse,
 * device preview, right-panel tab) - none of them touch the server or existing state.
 */
@Component({
  selector: 'app-template-editor-v2',
  imports: [...TEMPLATE_EDITOR_V2_IMPORTS],
  providers: [ConfirmationService, MessageService, ReviewStoreV2, EditorHistoryV2],
  templateUrl: './template-editor-v2.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './template-editor-v2.component.scss',
})
export class TemplateEditorV2Component extends BaseTemplateEditorComponent {
  /** Subject/Preheader should be visible from the moment the editor opens, even
   *  before a template name is typed/confirmed - the base class only adds them
   *  once a name is picked (see handleNewTemplate()). Only kicks in for a blank
   *  "no name in the route yet" load; an existing/new-named template still goes
   *  through the normal fetch flow untouched. */
  protected override onInitComponentSpecificBehaviour(): void {
    if (!this.route.snapshot.paramMap.get('templateName')) {
      this.initializeDefaultBlocks();
      this.editorHistory.reset(this.snapshotV2());
    }

    this.reviewStore.state$.pipe(takeUntil(this._destroy$)).subscribe((state) => (this.reviewStateV2 = state));
    // A pin clicked on the live canvas opens its comment in the Comments tab
    this.reviewStore.focusComment$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      if (!this.reviewActiveV2) {
        this.rightPanelTab = 'comments';
      }
    });

    // Capture phase, so this runs before Quill's own Ctrl+Z/Ctrl+Y handling.
    fromEvent<KeyboardEvent>(document, 'keydown', { capture: true })
      .pipe(takeUntil(this._destroy$))
      .subscribe(event => this.onUndoRedoKeyV2(event));
  }

  // --- Undo/redo history (component-scoped, see EditorHistoryV2) ---
  protected readonly editorHistory = inject(EditorHistoryV2);

  // --- Review (Proof/Annotate): versions, version-scoped comments and decisions.
  //     All of it lives in the component-scoped ReviewStoreV2; this class only
  //     connects it to the template lifecycle (load, save, lock) ---
  protected readonly reviewStore = inject(ReviewStoreV2);
  protected reviewStateV2: ReviewStateV2 = INITIAL_REVIEW_STATE;
  protected versionDialogV2: VersionDialogV2 | null = null;
  protected versionBusyV2 = false;
  /** Set while "New version" waits for the template to be saved first. */
  private pendingVersionV2: { comment: string | null } | null = null;
  private shareParamsAppliedV2 = false;

  protected get reviewActiveV2(): boolean {
    return this.reviewStateV2.mode !== 'off';
  }

  protected get languageLabelV2(): string {
    return (this.selectedLanguage || 'en').toUpperCase();
  }

  /** Proof shows the viewed version's frozen blocks instead of the live ones. */
  protected get reviewAllBlocksV2(): any[] {
    return this.reviewStateV2.viewingBlocks ?? [];
  }

  protected get reviewPreviewBlocksV2(): any[] {
    return this.reviewAllBlocksV2.filter((b: any) => b.name !== 'subject' && b.name !== 'preheader');
  }

  protected get shareUrlV2(): string | null {
    const version = this.reviewStateV2.viewing ?? this.reviewStateV2.latest;
    if (!this.templateName || this.isNew || version === null) {
      return null;
    }
    const tree = this.router.createUrlTree([`${this.templateBasePath}${this.templateName}`, this.selectedLanguage], {
      queryParams: { mode: 'proof', version },
    });
    return new URL(this.router.serializeUrl(tree), window.location.origin).toString();
  }

  protected override handleTemplateDataResponse(res: any): void {
    super.handleTemplateDataResponse(res);
    this.editorHistory.reset(this.snapshotV2());
    this.reviewStore.init(this.isNew ? null : this.idcustom, this.selectedLanguage);
    this.applyShareParamsV2();
  }

  /** A shared link (?mode=proof&version=N) opens Proof on that version, once. */
  private applyShareParamsV2(): void {
    if (this.shareParamsAppliedV2 || this.isNew) {
      return;
    }
    this.shareParamsAppliedV2 = true;
    const params = this.route.snapshot.queryParamMap;
    if (params.get('mode') !== 'proof') {
      return;
    }
    this.reviewStore.setMode('proof');
    const version = Number(params.get('version'));
    if (Number.isInteger(version) && version > 0) {
      this.reviewStore.selectVersion(version);
    }
  }

  protected setReviewModeV2(mode: ReviewModeV2): void {
    this.reviewStore.setMode(mode);
  }

  /** Edit/View from the canvas toolbar also leave Proof/Annotate. */
  protected onSetViewModeV2(readonlyRequested: boolean): void {
    this.reviewStore.setMode('off');
    this.setViewModeV2(readonlyRequested);
  }

  protected openCreateVersionDialogV2(): void {
    this.versionDialogV2 = { mode: 'create', target: (this.reviewStateV2.latest ?? 0) + 1 };
  }

  protected openRestoreDialogV2(version: number): void {
    this.versionDialogV2 = { mode: 'restore', target: version };
  }

  protected confirmVersionDialogV2(comment: string | null): void {
    if (!this.versionDialogV2) {
      return;
    }
    if (this.versionDialogV2.mode === 'create') {
      this.requestCreateVersionV2(comment);
    } else {
      this.restoreVersionV2(this.versionDialogV2.target, comment);
    }
  }

  /** A version snapshots the template as saved on the server, so a new or
   *  changed template is saved first and the version is created once that
   *  save succeeds (see flushPendingVersionV2()). */
  protected requestCreateVersionV2(comment: string | null): void {
    if (this.isNew || this.hasUnsavedChangesV2) {
      this.versionDialogV2 = null;
      this.pendingVersionV2 = { comment };
      this.onSubmitBlocks();
      return;
    }
    this.createVersionNowV2(comment);
  }

  private createVersionNowV2(comment: string | null): void {
    this.versionBusyV2 = true;
    this.reviewStore
      .createVersion(comment)
      .pipe(finalize(() => (this.versionBusyV2 = false)))
      .subscribe({
        next: (version) => {
          this.versionDialogV2 = null;
          this.toastBarService.showToastSuccess(`✓ Version ${version.version} pulled into review`);
        },
        error: (error: HttpErrorResponse) => {
          this.toastBarService.showToastError(error?.error?.error || 'Could not create the version.');
        },
      });
  }

  private restoreVersionV2(version: number, comment: string | null): void {
    this.versionBusyV2 = true;
    this.reviewStore
      .restoreVersion(version, comment)
      .pipe(finalize(() => (this.versionBusyV2 = false)))
      .subscribe({
        next: (restored) => {
          this.versionDialogV2 = null;
          this.hasUnsavedChangesV2 = false;
          // The server overwrote the working blocks - reload them in place
          this.fetchTemplateData();
          this.toastBarService.showToastSuccess(`✓ Version ${version} restored as Version ${restored.version}`);
        },
        error: (error: HttpErrorResponse) => {
          this.toastBarService.showToastError(
            error?.status === 409
              ? 'Lock this language before restoring.'
              : error?.error?.error || 'Could not restore the version.'
          );
        },
      });
  }

  private flushPendingVersionV2(): void {
    const pending = this.pendingVersionV2;
    this.pendingVersionV2 = null;
    if (pending) {
      this.createVersionNowV2(pending.comment);
    }
  }

  /** Plain saves never create a version, even if an earlier save-then-version
   *  attempt was cancelled or failed validation. */
  protected saveV2(isResponsysSave = false): void {
    this.pendingVersionV2 = null;
    this.onSubmitBlocks(isResponsysSave);
  }

  protected override handleTemplateCreationResponse(): void {
    super.handleTemplateCreationResponse();
    this.hasUnsavedChangesV2 = false;
    this.reviewStore.init(this.idcustom, this.selectedLanguage);
    this.flushPendingVersionV2();
    this.editorHistory.reset(this.snapshotV2());
  }

  /** Block types with a dedicated always-visible v2 form/summary component -
   *  every instance stays visible and editable in both the center canvas and
   *  the right panel simultaneously (like Subject/Preheader), instead of the
   *  master-detail "one at a time" behavior the rest of the blocks use.
   *  Extend this set as more blocks get their own v2 component. */
  private readonly alwaysVisibleBlockNames = new Set([
    'main-banner',
    'paragraph-cta',
    'heading',
    'only-intro',
    'only-cta',
    'property-description',
    'x2-prop-rec',
    'counter',
    'property-usp-1x-photo',
    'property-usp-2x-photo',
    'x2-experiences',
    'inspiration',
    'x2-prop-rec-onp',
    'reviews',
    'zigzag-heading-and-cta',
    'zigzag-heading-and-alt-cta',
    'zigzag-no-heading-no-button',
    'x3-experiences',
    'event',
    'only-2-photos',
    'voucher',
    'gift-voucher',
    'experience-concierge',
    'moments',
    'reservation',
    'check-in',
    'disclaimer',
    'valamar-app',
  ]);

  protected get alwaysVisibleBlocks(): any[] {
    return this.selectedBlocks.filter((b: any) => this.alwaysVisibleBlockNames.has(b.name));
  }

  // --- v2-only, purely presentational UI state (not part of the shared base class) ---
  protected selectedBlockId: string | null = null;
  protected sidebarCollapsed = localStorage.getItem('cr-sidebar-collapsed-v2') === 'true';
  protected deviceMode: DeviceModeV2 = 'desktop';
  protected rightPanelTab: RightPanelTabV2 = 'properties';

  /** True once any block content/order/template name changes locally - reset
   *  on a successful save. Drives the "unsaved changes" confirm on Back (AC6,
   *  RCA-390) since the base class has no dirty-tracking of its own. */
  protected hasUnsavedChangesV2 = false;


  protected override onBlockDataUpdated(data: any): void {
    this.hasUnsavedChangesV2 = true;
    super.onBlockDataUpdated(data);
    // Keyed by block, so a typing burst in one block is a single undo step.
    this.editorHistory.record(this.snapshotV2(), `edit:${data.blockId}`);
  }

  /** Serialises the current editor state for EditorHistoryV2. Blocks become
   *  JSON strings and the media queues are copied, so later in-place edits
   *  can't change a snapshot that's already in history. */
  private snapshotV2(): EditorSnapshotV2 {
    return {
      blocks: this.selectedBlocks.map(block => JSON.stringify(block)),
      mediaForDeletion: [...this.mediaForDeletion],
      mediaForDeletionWithinABlock: [...this.mediaForDeletionWithinABlock],
    };
  }

  protected undoV2(): void {
    const snapshot = this.editorHistory.undo();
    if (snapshot) {
      this.applySnapshotV2(snapshot);
    }
  }

  protected redoV2(): void {
    const snapshot = this.editorHistory.redo();
    if (snapshot) {
      this.applySnapshotV2(snapshot);
    }
  }

  private onUndoRedoKeyV2(event: KeyboardEvent): void {
    if (!(event.ctrlKey || event.metaKey)) {
      return;
    }

    const key = event.key.toLowerCase();
    const isUndo = key === 'z';
    const isRedo = key === 'y';
    if (!isUndo && !isRedo) {
      return;
    }

    // Same places the buttons are missing or covered.
    if (this.reviewActiveV2 || this.exportDialogOpenV2 || this.versionDialogV2 || this.sidebarAddBlockOpen) {
      return;
    }

    // Cancelled even when there's nothing to undo: Quill skips default-prevented keys.
    event.preventDefault();

    if (isUndo) {
      this.undoV2();
    } else {
      this.redoV2();
    }
  }

  /** Turns a snapshot's JSON strings back into blocks. A block whose JSON is
   *  unchanged keeps its existing object, so only the changed blocks become
   *  new objects. */
  private applySnapshotV2(snapshot: EditorSnapshotV2): void {
    const currentJson = this.selectedBlocks.map(block => JSON.stringify(block));
    this.selectedBlocks = snapshot.blocks.map(json => {
      const index = currentJson.indexOf(json);
      return index !== -1 ? this.selectedBlocks[index] : (JSON.parse(json) as BlockModel);
    });

    this.globalTemplateService.restoreMediaForDeletion(
      snapshot.mediaForDeletion,
      snapshot.mediaForDeletionWithinABlock,
    );
  }

  protected override handleSuccessfulTemplateUpdate(response: any): void {
    this.hasUnsavedChangesV2 = false;
    super.handleSuccessfulTemplateUpdate(response);
    this.flushPendingVersionV2();
    this.editorHistory.reset(this.snapshotV2());
  }

  protected onTemplateNameChangeV2(name: string | null): void {
    this.templateName = name;
    this.hasUnsavedChangesV2 = true;
  }

  /** Back button entry point (replaces the base class's direct goBack()) -
   *  warns via modal when there are unsaved changes, otherwise leaves immediately. */
  protected confirmGoBackV2(unsavedChangesModal: TemplateRef<any>): void {
    if (this.hasUnsavedChangesV2) {
      this.activeModal = this.modalService.open(unsavedChangesModal, { centered: true, backdrop: 'static' });
    } else {
      this.goBack();
    }
  }

  protected leaveWithoutSavingV2(): void {
    this.activeModal.close();
    this.goBack();
  }

  /** Export-content dialog (AC1-4, RCA-404) - a self-contained *_v2 component
   *  (see export-dialog-v2) instead of the base class's outputTextContent()/
   *  #exportModal ng-template, since the mockup's popup has its own bespoke
   *  chrome (centered heading, info-strip summary, option cards) that doesn't
   *  map onto the shared bootstrap modal used elsewhere. */
  protected exportDialogOpenV2 = false;

  protected get sidebarBlocks(): any[] {
    return this.selectedBlocks;
  }

  protected get selectedBlockObj(): any | undefined {
    return this.selectedBlocks.find((b: any) => b.blockId === this.selectedBlockId);
  }

  /** Computed here (Default/Eager strategy, so re-evaluated every tick) and
   *  passed down as a plain boolean - lockedBy/currentUser are mutated in
   *  place (see sendLockedBy()), so passing those objects straight through to
   *  the OnPush preview-v2/add-block-v2 chain never re-triggers their change
   *  detection after a lock/unlock (same object reference, no visible change). */
  protected get addBlockDisabledV2(): boolean {
    return !this.isNew && this.lockedBy?.username !== this.currentUser?.username;
  }

  /** Mockup's sidebar "+ Add new block" button (Region 2) - a separate trigger/
   *  modal pair from preview-v2's own per-gap "+" buttons (Region 5), but
   *  reuses the exact same app-add-block-modal-v2 + insertion contract: the
   *  modal inserts right after whatever block it's given as [blockData], so
   *  passing the LAST real block always appends the new one at the end. */
  protected sidebarAddBlockOpen = false;

  protected get sidebarAddBlockContext(): any {
    return this.selectedBlocks[this.selectedBlocks.length - 1];
  }

  protected openSidebarAddBlockV2(): void {
    if (this.addBlockDisabledV2) {
      return;
    }
    this.sidebarAddBlockOpen = true;
  }

  protected onSidebarAddBlockSelected(event: any): void {
    this.handleBlockChangeEvent(event);
    this.sidebarAddBlockOpen = false;
  }

  /** Everything shown as pure preview in the center canvas: every block except the one
   *  currently open in the right panel, and except the invisible Subject/Preheader
   *  system fields (matching the existing app-preview route, which excludes them too).
   *  alwaysVisibleBlockNames are exempt from the "hide while selected" rule: unlike
   *  the generic master-detail blocks, their center-canvas rendering is a dedicated
   *  read-only summary, not the real component - so there's no double-mount risk in
   *  showing them in both places at once. */
  protected get previewBlocks(): any[] {
    return this.selectedBlocks.filter(
      (b: any) =>
        (b.blockId !== this.selectedBlockId || this.alwaysVisibleBlockNames.has(b.name)) &&
        b.name !== 'subject' &&
        b.name !== 'preheader'
    );
  }

  protected get sidebarDragDisabled(): boolean {
    return !this.enableDragDrop || this.isReadonly();
  }

  protected selectBlockV2(blockId: string): void {
    // Proof/Annotate show frozen version content - nothing to open for editing
    if (this.reviewActiveV2) {
      return;
    }
    this.selectedBlockId = this.selectedBlockId === blockId ? null : blockId;
    // Stay on Comments if that's already open (e.g. clicking a block to
    // comment on it) - only default to Properties otherwise.
    if (this.rightPanelTab !== 'comments') {
      this.rightPanelTab = 'properties';
    }
  }

  /** Unlike selectBlockV2 (sidebar click, toggles), this just sets the active
   *  block - used when focusing into the always-mounted Subject/Preheader
   *  inputs, so the sidebar row and preview summary highlight while typing. */
  protected focusBlockV2(blockId: string): void {
    this.selectedBlockId = blockId;
  }

  /** A canvas field (appInlineEdit) was left with new text. The block's
   *  right-panel form only reads its text when it's created, and it's keyed by
   *  block object (like after undo/redo), so swapping in an identical copy of
   *  the block re-creates the form with that text. */
  protected onInlineEditCommitV2(blockId: string): void {
    this.selectedBlocks = this.selectedBlocks.map((block) =>
      block.blockId === blockId && block.variables
        ? { ...block, variables: { ...block.variables } }
        : block
    );
  }

  /** After inserting a block (from the add-block-modal-v2 catalog), immediately
   *  open it in the right panel and highlight it in the sidebar/preview, same
   *  as clicking it there would - matching the mockup's "add block" flow. */
  protected override handleBlockChangeEvent(blockChangeEvent: any): void {
    const lengthBefore = this.selectedBlocks.length;
    this.hasUnsavedChangesV2 = true;
    super.handleBlockChangeEvent(blockChangeEvent);
    if (this.selectedBlocks.length > lengthBefore) {
      const newBlock = this.selectedBlocks[blockChangeEvent.spliceIndex];
      if (newBlock) {
        this.selectedBlockId = newBlock.blockId;
        this.rightPanelTab = 'properties';
      }
    }
  }

  protected closeSelectedBlockV2(): void {
    this.selectedBlockId = null;
  }

  protected onRemoveBlockV2(blockId: string): void {
    if (this.selectedBlockId === blockId) {
      this.selectedBlockId = null;
    }
    this.hasUnsavedChangesV2 = true;
    this.removeBlock(blockId);
    // A blocked delete (minimum count) changes nothing, so record() skips it.
    this.editorHistory.record(this.snapshotV2());
  }

  /** Inserts a full copy of the block (all its entered content, via a deep
   *  clone of `variables`) directly after the original - can't reuse the base
   *  class's handleBlockClick() for this since it always starts a block with
   *  empty variables. Comments/pins are NOT carried over (notifications reset
   *  to empty), since they'd otherwise still reference the original block's id. */
  protected duplicateBlockV2(blockId: string): void {
    const source = this.selectedBlocks.find((b: any) => b.blockId === blockId);
    if (!source) {
      return;
    }

    const limit = this.blockMaximumCount.find((b) => b.blockName === source.name)?.limit;
    const existingCount = this.selectedBlocks.filter((b: any) => b.name === source.name).length;
    if (limit !== undefined && existingCount >= limit) {
      this.toastBarService.showToastError(
        `Can't add this element, reached the limit of elements with this block which is ${limit}`
      );
      return;
    }

    const maxCount = Math.max(
      0,
      ...this.selectedBlocks.filter((b: any) => b.name === source.name).map((b: any) => b.count || 0)
    );
    const newCount = maxCount + 1;
    const newBlockId = Helper.generateIdHash(
      `${this.templateName}-${this.selectedLanguage}-${source.name}-${newCount}-${Date.now()}-${Math.random()}`
    );

    const duplicate = {
      ...JSON.parse(JSON.stringify(source)),
      blockId: newBlockId,
      count: newCount,
      notifications: [],
    };

    this.selectedBlocks.splice(this.selectedBlocks.indexOf(source) + 1, 0, duplicate);
    if (!this.isNew) {
      this.reorder = true;
    }
    this.hasUnsavedChangesV2 = true;
  }

  /** CDK's previousIndex/currentIndex are plain positions among ALL registered
   *  cdkDrag items (locked ones included) - locking only stops a locked item
   *  from being picked UP itself, it doesn't stop a dragged item from being
   *  DROPPED into a locked item's slot and shoving it out of place. Since
   *  Subject/Preheader must always stay fixed at the front, currentIndex is
   *  clamped so a drop can never land inside that locked zone. */
  protected onSidebarReorderV2(event: CdkDragDrop<string[]>): void {
    const lockedCount = this.selectedBlocks.filter((b: any) => LOCKED_BLOCK_NAMES.has(b?.name)).length;
    this.hasUnsavedChangesV2 = true;
    this.drop({
      ...event,
      currentIndex: Math.max(event.currentIndex, lockedCount),
    });
    this.editorHistory.record(this.snapshotV2());
  }

  protected toggleSidebarV2(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    localStorage.setItem('cr-sidebar-collapsed-v2', String(this.sidebarCollapsed));
  }

  protected setDeviceModeV2(mode: DeviceModeV2): void {
    this.deviceMode = mode;
  }

  protected setRightPanelTabV2(tab: RightPanelTabV2): void {
    this.rightPanelTab = tab;
  }

  // --- Lock status (reuses the existing sendLockedBy()/lockedBy state 1:1 - see the
  //     #annotate-menu banner in template-editor.component.html for the original) ---
  protected get lockMenuVisibleV2(): boolean {
    return (
      !this.isTranslated &&
      (!this.isBeingTranslated || this.isTranslator) &&
      (!this.guardByProcessing || !this.isTemplateProcToResponsys) &&
      !this.isCopy &&
      (!this.showMetaSection || (!this.isApproved && this.metaCanEdit))
    );
  }

  /** Unlike v1 (which hides the lock UI entirely for isNew - see #annotate-menu),
   *  the mockup always shows the lock pill, including for a brand-new/unsaved
   *  template. So this v2-only getter drops the `!this.isNew` gate v1 has. */
  protected get lockInfoV2(): { locked: boolean; mine: boolean; label: string } | null {
    if (!this.lockMenuVisibleV2) {
      return null;
    }
    if (this.lockedBy.username) {
      const mine = this.lockedBy.username === this.currentUser?.username;
      return { locked: true, mine, label: this.lockedBy.name || '' };
    }
    return { locked: false, mine: false, label: '' };
  }

  protected get warningMessageV2(): string | null {
    if (this.isBeingTranslated && !this.isTranslator) {
      return "This template is being translated! You can't edit it now!";
    }
    if (this.guardByProcessing && this.isTemplateProcToResponsys) {
      return "This template is being processed! You can't edit it now!";
    }
    if (this.isCopy) {
      return 'Scroll to bottom to confirm the copy process!';
    }
    return null;
  }

  /** Backs the canvas toolbar's Edit/View segmented toggle. Switching to View
   *  (readonly) is always safe, but switching to Edit must never be possible
   *  without actually holding the lock - otherwise anyone could bypass the
   *  lock entirely just by clicking this button (see lockMenuVisibleV2/
   *  toggleLockV2 for how the lock itself is taken). */
  protected setViewModeV2(readonlyRequested: boolean): void {
    if (!readonlyRequested && !this.isNew && !this.isMyLock()) {
      return;
    }
    this.toogleDisableEdit(readonlyRequested ? this.READONLY : this.EDIT);
  }

  protected toggleLockV2(): void {
    const info = this.lockInfoV2;
    if (!info) {
      return;
    }
    // Nothing persisted yet for a brand-new template (no idcustom) - there's no
    // real lock to toggle server-side until a name is set, unlike v1 where this
    // whole control is hidden until then (see lockInfoV2 above).
    if (!this.idcustom) {
      this.toastBarService.showToast({
        severity: 'info',
        summary: 'Info',
        detail: 'Enter a template name before locking the template.',
      });
      return;
    }
    if (info.locked && info.mine) {
      this.sendLockedBy();
    } else if (!info.locked) {
      this.sendLockedBy(this.currentUser);
    }
  }

  // --- Navbar/channel-navbar disabled-state + label derivations, mirroring the
  //     exact expressions already used in template-editor.component.html 1:1 ---
  protected get deleteDisabledV2(): boolean {
    return !!(
      this.isNew ||
      this.isCopy ||
      (!this.isMyLock() && (!this.guardByProcessing || this.isTemplateProcToResponsys))
    );
  }

  protected get exportDisabledV2(): boolean {
    return !!(this.selectedBlocks.length === 0 || (this.guardByProcessing && this.isTemplateProcToResponsys));
  }

  protected get duplicateDisabledV2(): boolean {
    return !this.isMyLock() || this.isCopy;
  }

  protected get translateDisabledV2(): boolean {
    return (
      this.isApproved ||
      (this.guardByProcessing && this.isTemplateProcToResponsys) ||
      this.isCopy ||
      this.isNew
    );
  }

  protected get connectCampaignDisabledV2(): boolean {
    return !this.templateName || this.templateName.trim() === '';
  }

  protected get showConnectCampaignV2(): boolean {
    return !this.isNew && !this.isTranslator && this.selectedLanguage === 'en';
  }

  protected get saveResponsysDisabledV2(): boolean {
    return !!(!(this.lockedBy.username !== '' && this.lockedBy.username != null) || this.isTemplateProcToResponsys);
  }

  protected get saveDisabledV2(): boolean {
    return this.isSubmitDisabled || this.isReadonly();
  }

  protected get saveLabelV2(): string {
    if (this.isTranslator) {
      return 'Update translation';
    }
    if (this.isCopy) {
      return 'Confirm template copy';
    }
    return 'Save';
  }
}
